@if (profileLoading()) {
  <div class="profile-loading">
    <p-progressSpinner></p-progressSpinner>
    <p i18n="@@profile.loading">Loading profile data...</p>
  </div>
} @else if (profileError()) {
  <div class="profile-error">
    <i class="pi pi-exclamation-circle profile-error-icon"></i>
    <h3 i18n="@@profile.error.title">Failed to load profile</h3>
    <p i18n="@@profile.error.message">Unable to retrieve your profile data. Please try again.</p>
    <div class="profile-error-actions">
      <p-button
        i18n-label="@@common.logout"
        label="Logout"
        icon="pi pi-sign-out"
        severity="secondary"
        [outlined]="true"
        (onClick)="logout()">
      </p-button>
      <p-button
        i18n-label="@@common.tryAgain"
        label="Try again"
        icon="pi pi-refresh"
        (onClick)="loadProfile()">
      </p-button>
    </div>
  </div>
} @else {
<div class="layout-container" [class.assistant-open]="canShowAssistant() && assistantOpen()">
  <!-- Desktop Sidebar -->
  <aside class="sidebar" [class.collapsed]="sidebarCollapsed()">
    <!-- Signed In As Indicator - Desktop -->
    @if (signedInAsEmail()) {
      <button type="button" class="signed-in-as-indicator" (click)="navigateToAdminSignIn()">
        <i class="pi pi-user"></i>
        @if (!sidebarCollapsed()) {
          <span class="signed-in-text">
            <span class="signed-in-label" i18n="@@signedInAs">Signed in as</span>
            <span class="signed-in-email">{{ signedInAsEmail() }}</span>
          </span>
        }
      </button>
    }

    <div class="sidebar-header">
      <p-button
        icon="pi pi-bars"
        [outlined]="true"
        severity="secondary"
        size="small"
        i18n-ariaLabel="@@nav.toggleSidebar"
        ariaLabel="Collapse sidebar"
        class="collapse-btn"
        (onClick)="toggleCollapse()">
      </p-button>

      <div class="logo" [class.collapsed]="sidebarCollapsed()">
        <div class="logo-icon">
          <i class="pi pi-microphone"></i>
        </div>
        @if (!sidebarCollapsed()) {
          <span>{{ appName }}</span>
        }
      </div>
    </div>

    <div class="sidebar-controls" [class.collapsed]="sidebarCollapsed()">
      <button
        type="button"
        class="sidebar-trigger"
        [attr.aria-label]="currentLanguageLabel()"
        (click)="toggleLanguageMenu($event)">
        <i class="pi pi-language"></i>
        @if (!sidebarCollapsed()) {
          <span class="sidebar-trigger-label">{{ currentLanguageLabel() }}</span>
          <i class="pi pi-chevron-down sidebar-trigger-chevron"></i>
        }
      </button>
      <p-menu #languageMenu [model]="languageMenuItems()" [popup]="true" appendTo="body"></p-menu>

      @if (spaceService.loaded()) {
        <button
          type="button"
          class="sidebar-trigger"
          [attr.aria-label]="selectWorkspaceLabel"
          (click)="toggleSpaceMenu($event)">
          <i class="pi pi-briefcase"></i>
          @if (!sidebarCollapsed()) {
            <span class="sidebar-trigger-label">{{ spaceService.currentSpace()?.name || selectWorkspaceLabel }}</span>
            <i class="pi pi-chevron-down sidebar-trigger-chevron"></i>
          }
        </button>
        <p-menu #spaceMenu [model]="spaceMenuItems()" [popup]="true" appendTo="body">
          <ng-template #item let-item>
            <button type="button" class="space-menu-item" (click)="item.command && item.command()">
              <i [class]="item.icon"></i>
              <span>{{ item.label }}</span>
              @if (item.id) {
                <i class="pi pi-cog space-settings-icon" (click)="goToSpaceSettings(item.id, $event)"></i>
              }
            </button>
          </ng-template>
        </p-menu>
      }

      <!-- Theme switcher: light / dark / follow system -->
      <div class="theme-switch" role="group" i18n-aria-label="@@theme.label" aria-label="Theme">
        @for (option of themeOptions; track option.mode) {
          <button
            type="button"
            class="theme-switch-btn"
            [class.active]="themeService.mode() === option.mode"
            [attr.aria-label]="option.label"
            [attr.aria-pressed]="themeService.mode() === option.mode"
            [pTooltip]="option.label"
            tooltipPosition="top"
            (click)="setTheme(option.mode)">
            <i [class]="option.icon"></i>
          </button>
        }
      </div>
    </div>

    <nav class="sidebar-nav" i18n-aria-label="@@nav.main" aria-label="Main navigation">
      <ul>
        @for (item of menuItems(); track item.label) {
          <li>
            @if (item.externalUrl) {
              <a [href]="item.externalUrl" target="_blank" rel="noopener">
                <i [class]="item.icon"></i>
                @if (!sidebarCollapsed()) {
                  <span>{{ item.label }}</span>
                }
              </a>
            } @else {
              <a [routerLink]="item.routerLink" routerLinkActive="active"
                 [pTooltip]="item.warningIcon && subscriptionService.hasInvoicesRequiringAttention ? item.warningTooltip : ''"
                 tooltipPosition="right">
                @if (item.warningIcon && subscriptionService.hasInvoicesRequiringAttention) {
                  <i [class]="item.warningIcon" class="warning-icon"></i>
                } @else {
                  <i [class]="item.icon"></i>
                }
                @if (!sidebarCollapsed()) {
                  <span>{{ item.label }}</span>
                }
              </a>
            }
          </li>
        }
      </ul>
    </nav>

    <div class="sidebar-footer">
      <p-button
        icon="pi pi-sign-out"
        [label]="sidebarCollapsed() ? '' : logoutLabel"
        [ariaLabel]="logoutLabel"
        severity="secondary"
        [text]="true"
        size="small"
        class="logout-btn"
        (onClick)="logout()">
      </p-button>
    </div>
  </aside>

  <!-- Mobile app bar -->
  <header class="mobile-appbar">
    <div class="mobile-brand">
      <span class="logo-icon"><i class="pi pi-microphone"></i></span>
      <span class="mobile-brand-name">{{ appName }}</span>
    </div>

    <div class="mobile-appbar-actions">
      @if (signedInAsEmail()) {
        <button
          type="button"
          class="signed-in-as-chip"
          i18n-aria-label="@@signedInAs"
          aria-label="Signed in as"
          (click)="navigateToAdminSignIn()">
          <i class="pi pi-user"></i>
          <span>{{ signedInAsEmail() }}</span>
        </button>
      }
      @if (canShowAssistant()) {
        <button
          type="button"
          class="appbar-btn ask-agent-btn"
          [class.active]="assistantOpen()"
          [attr.aria-pressed]="assistantOpen()"
          [attr.aria-label]="askAgentLabel"
          (click)="toggleAssistant()">
          <i class="pi pi-sparkles"></i>
        </button>
      }
      <button
        type="button"
        class="appbar-btn"
        [attr.aria-label]="themeLabel()"
        (click)="toggleTheme()">
        <i class="pi" [class.pi-moon]="!themeService.isDark()" [class.pi-sun]="themeService.isDark()"></i>
      </button>
      @if (spaceService.loaded()) {
        <button
          type="button"
          class="appbar-btn workspace-btn"
          [attr.aria-label]="selectWorkspaceLabel"
          (click)="toggleMobileSpaceMenu($event)">
          <i class="pi pi-briefcase"></i>
          <span class="workspace-name">{{ spaceService.currentSpace()?.name || selectWorkspaceLabel }}</span>
        </button>
        <p-menu #mobileSpaceMenu [model]="spaceMenuItems()" [popup]="true" appendTo="body">
          <ng-template #item let-item>
            <button type="button" class="space-menu-item" (click)="item.command && item.command()">
              <i [class]="item.icon"></i>
              <span>{{ item.label }}</span>
              @if (item.id) {
                <i class="pi pi-cog space-settings-icon" (click)="goToSpaceSettings(item.id, $event)"></i>
              }
            </button>
          </ng-template>
        </p-menu>
      }
    </div>
  </header>

  <!-- Рабочая область: контент + док-панель агента справа. Панель не
       оверлей — открывается, и контент ужимается, чтобы агент и
       пользователь смотрели на одну и ту же страницу. -->
  <div class="workspace">
    <div class="content-column">
      <!-- Вход в режим агента — только для юзеров с ролью ai-chat или admin
           в speaknode-client (см. AuthService.hasAnyClientRole). Без роли
           верхней полосы нет вовсе, раскладка остаётся прежней.

           При открытой панели полосы тоже нет: кнопка «спросить агента»
           рядом с самим агентом — лишний элемент, а её место отдаётся
           странице. Закрывается панель своим крестиком, и полоса
           возвращается. -->
      <div class="content-topbar">
        <!-- Ссылки на документацию слева, кнопка агента справа: полоса
             существует и без агента, поэтому ссылки не пропадают ни когда
             агент выключен, ни когда его панель открыта. -->
        <nav class="topbar-docs" i18n-aria-label="@@nav.docs" aria-label="Documentation">
          @for (link of docsLinks; track link.page) {
            <a
              class="topbar-docs-link"
              [href]="link.url"
              target="_blank"
              rel="noopener noreferrer">
              <i class="pi pi-book" aria-hidden="true"></i>
              <span>{{ link.label }}</span>
            </a>
          }
        </nav>

        @if (canShowAssistant() && !assistantOpen()) {
          <button
            type="button"
            class="ask-agent"
            [class.active]="assistantOpen()"
            [attr.aria-pressed]="assistantOpen()"
            (click)="toggleAssistant()">
            <i class="pi pi-sparkles"></i>
            <span i18n="@@assistant.ask">Ask agent</span>
            @if (assistantPending()) {
              <span class="ask-agent-badge" aria-hidden="true"></span>
            }
          </button>
        }
      </div>

      <!-- Main Content -->
      <main class="main-content">
        <router-outlet></router-outlet>
      </main>
    </div>

    <!-- Панель живёт в разметке и закрытой: иначе она возникала бы и
         исчезала мгновенно, а ширину нельзя анимировать у того, чего в
         DOM ещё нет. Закрытая она нулевой ширины, вне фокуса и вне
         доступности. -->
    @if (canShowAssistant()) {
      <app-assistant-panel
        [class.closed]="!assistantOpen()"
        [attr.inert]="assistantOpen() ? null : ''"
        [attr.aria-hidden]="assistantOpen() ? null : 'true'"></app-assistant-panel>
    }
  </div>

  <!-- Mobile bottom tab bar -->
  <nav class="mobile-tabbar" i18n-aria-label="@@nav.main" aria-label="Main navigation">
    @for (item of primaryMenuItems(); track item.label) {
      <a class="tab" [routerLink]="item.routerLink" routerLinkActive="active">
        <i [class]="item.icon"></i>
        <span>{{ item.label }}</span>
      </a>
    }
    <button
      type="button"
      class="tab"
      [class.active]="sidebarVisible()"
      [attr.aria-expanded]="sidebarVisible()"
      (click)="toggleSidebar()">
      <i class="pi pi-ellipsis-h"></i>
      <span>{{ moreLabel }}</span>
      @if (subscriptionService.hasInvoicesRequiringAttention) {
        <span class="tab-badge" aria-hidden="true"></span>
      }
    </button>
  </nav>

  <!-- Mobile "More" sheet -->
  <p-drawer
    [(visible)]="sidebarVisible"
    position="bottom"
    styleClass="mobile-sheet"
    [header]="moreLabel">
    <div class="sheet-controls">
      <button
        type="button"
        class="sheet-trigger"
        [attr.aria-label]="currentLanguageLabel()"
        (click)="toggleMobileLanguageMenu($event)">
        <i class="pi pi-language"></i>
        <span class="sheet-trigger-label">{{ currentLanguageLabel() }}</span>
        <i class="pi pi-chevron-down"></i>
      </button>
      <p-menu #mobileLanguageMenu [model]="languageMenuItems()" [popup]="true" appendTo="body"></p-menu>

      <div class="theme-switch" role="group" i18n-aria-label="@@theme.label" aria-label="Theme">
        @for (option of themeOptions; track option.mode) {
          <button
            type="button"
            class="theme-switch-btn"
            [class.active]="themeService.mode() === option.mode"
            [attr.aria-label]="option.label"
            [attr.aria-pressed]="themeService.mode() === option.mode"
            (click)="setTheme(option.mode)">
            <i [class]="option.icon"></i>
            <span>{{ option.label }}</span>
          </button>
        }
      </div>
    </div>

    <ul class="sheet-nav">
      @for (item of secondaryMenuItems(); track item.label) {
        <li>
          @if (item.externalUrl) {
            <a [href]="item.externalUrl" target="_blank" rel="noopener" (click)="closeSidebar()">
              <i [class]="item.icon"></i>
              <span>{{ item.label }}</span>
              <i class="pi pi-external-link sheet-nav-hint"></i>
            </a>
          } @else {
            <a [routerLink]="item.routerLink" routerLinkActive="active" (click)="closeSidebar()">
              @if (item.warningIcon && subscriptionService.hasInvoicesRequiringAttention) {
                <i [class]="item.warningIcon" class="warning-icon"></i>
              } @else {
                <i [class]="item.icon"></i>
              }
              <span>{{ item.label }}</span>
              <i class="pi pi-chevron-right sheet-nav-hint"></i>
            </a>
          }
        </li>
      }
    </ul>

    <div class="sheet-footer">
      <p-button
        i18n-label="@@common.logout"
        label="Logout"
        icon="pi pi-sign-out"
        severity="secondary"
        [outlined]="true"
        styleClass="w-full"
        (onClick)="logout()">
      </p-button>
    </div>
  </p-drawer>

  <!-- Toast Container. Кастомный message-template — рендерит кнопку
       Retry когда NotificationService.showRetriableError передал
       data.retry колбэк. -->
  <p-toast position="top-center">
    <ng-template let-message pTemplate="message">
      <div class="p-toast-message-text">
        <div class="p-toast-summary">{{ message.summary }}</div>
        @if (message.detail) {
          <div class="p-toast-detail">{{ message.detail }}</div>
        }
        @if (message.data?.retry) {
          <div class="toast-actions">
            <p-button
              [label]="message.data.retryLabel"
              icon="pi pi-refresh"
              size="small"
              severity="danger"
              [text]="true"
              (onClick)="message.data.retry(); clearMessage(message)">
            </p-button>
          </div>
        }
      </div>
    </ng-template>
  </p-toast>

</div>
}
